var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { applyMiddleware, compose, createStore, combineReducers } from 'redux';
/// <reference path="react-redux.d.ts"/>
import { useSelector as useSelectorInner } from 'react-redux';
var _window = typeof window === 'undefined' ? {} : window;
/**
 * This function takes an object containing an action creator and sub-reducer and binds them
 * together at the type level to create an action group.  At the JavaScript level it just passes
 * through the object unchanged, but at the type level it facilicates inference of all parts of the
 * action group and ensures that the types of the action creator and sub-reducer match.
 */
export var buildActionGroup = function (_a) {
    var actionCreator = _a.actionCreator, subReducer = _a.subReducer;
    return ({ actionCreator: actionCreator, subReducer: subReducer });
};
/**
 * An internal helper type that narrows the type of `actions` to an object of `ActionGroup`s.
 */
var buildReduxModule = function (initialState, actions) { return ({
    initialState: initialState,
    actions: actions,
}); };
/**
 * Given an object defining a Jantix module, performs static verification of its structure and
 * returns a narrowed version of it suitable for use in `buildStore`.
 *
 * @param initialState The initial state that will be provided to all of the action groups
 * @param actionGroups The action+sub-reducer pairs that will be grouped together under the created
 *        module and share their state.
 */
export var buildModule = function (initialState, actionGroups) {
    return buildReduxModule(initialState, actionGroups);
};
/**
 * Given an object defining the reducers and their internal action creators and sub-reducers, builds a Redux store and
 * typed functions for interacting with it.
 *
 * This function MUST be called with an explicit type parameter of `typeof modules` provided in order to gain fully-
 * typed results.  The syntax for that looks like this:
 *
 * ```ts
 * const yourModules = { ... };
 * const { dispatch, getState } = buildStore<typeof yourModules>(yourModules);
 * ```
 *
 * @param modules The object defining the reducers to construct
 * @param middleware An optional middleware object to add to the created store
 */
export var buildStore = function (modules, middleware, 
// eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
customReducers) {
    if (customReducers === void 0) { customReducers = {}; }
    var builtReducers = Object.keys(modules).reduce(function (acc, stateKey) {
        var _a;
        return (__assign({}, acc, (_a = {}, _a[stateKey] = function (state, action) {
            if (state === void 0) { state = modules[stateKey].initialState; }
            var subModule = modules[stateKey].actions[action.type];
            return subModule ? subModule.subReducer(state, action) : state;
        }, _a)));
    }, {});
    var actionCreators = Object.keys(modules).reduce(function (acc, stateKey) {
        var _a;
        return (__assign({}, acc, (_a = {}, _a[stateKey] = Object.keys(modules[stateKey].actions).reduce(function (acc, actionType) {
            var _a;
            return (__assign({}, acc, (_a = {}, _a[actionType] = modules[stateKey].actions[actionType].actionCreator, _a)));
        }, {}), _a)));
    }, {});
    var composeEnhancers = _window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    var store = createStore(combineReducers(__assign({}, builtReducers, customReducers)), middleware ? composeEnhancers(applyMiddleware(middleware)) : undefined);
    var dispatch = function (action) { return store.dispatch(action); };
    var getState = function () { return store.getState(); };
    var useSelector = function (selector, shallowEqual) {
        if (shallowEqual === void 0) { shallowEqual = function (prev, cur) { return prev === cur; }; }
        return useSelectorInner(selector, shallowEqual);
    };
    return { actionCreators: actionCreators, dispatch: dispatch, getState: getState, useSelector: useSelector, store: store };
};
//# sourceMappingURL=index.js.map